import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Hash, Save, AlertTriangle, CheckCircle2 } from 'lucide-react';

const buildPreview = (cfg, prefix, pad, seq, incMonth, incFy, incProject) => {
  const parts = [prefix];
  const period = (incMonth ? cfg.month : '') + (incFy ? cfg.fy : '');
  if (period) parts.push(period);
  parts.push(String(seq).padStart(pad, '0'));
  if (incProject) parts.push('ProjectName');
  return parts.join('-');
};

const InvoiceSeriesCard = ({ kind }) => {
  const title = kind === 'tax' ? 'Tax Invoice Numbering' : 'Proforma Invoice Numbering';
  const tid = `invoice-series-${kind}`;
  const [cfg, setCfg] = useState(null);
  const [prefix, setPrefix] = useState('');
  const [pad, setPad] = useState(3);
  const [nextSeq, setNextSeq] = useState('');
  const [incMonth, setIncMonth] = useState(true);
  const [incFy, setIncFy] = useState(true);
  const [incProject, setIncProject] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/invoices/series/${kind}`);
      setCfg(r.data);
      setPrefix(r.data.prefix);
      setPad(r.data.pad);
      setNextSeq(String(r.data.next_seq));
      setIncMonth(r.data.include_month);
      setIncFy(r.data.include_fy);
      setIncProject(r.data.include_project);
    } catch (err) {
      console.error('Failed to load invoice series', err);
    }
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanedPrefix = (prefix || '').trim().replace(/^-+|-+$/g, '');
  const seqInt = parseInt(nextSeq || '0', 10) || 0;

  const save = async () => {
    setMsg(null);
    if (!seqInt || seqInt < 1) return setMsg({ type: 'error', text: 'Enter a positive Next serial (e.g. 12).' });
    if (!cleanedPrefix) return setMsg({ type: 'error', text: 'Prefix is required (e.g. CC-TAX).' });
    setBusy(true);
    try {
      const r = await api.put(`/invoices/series/${kind}`, {
        prefix: cleanedPrefix, pad, next_seq: seqInt,
        include_month: incMonth, include_fy: incFy, include_project: incProject,
      });
      setMsg({ type: 'success', text: `Next ${kind} invoice will be ${r.data.next_code}.` });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Could not save.' });
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) {
    return (
      <div className="card p-6 mb-4" data-testid={`${tid}-card-loading`}>
        <h2 className="font-head text-xl font-bold mb-1 flex items-center gap-2" style={{ color: 'var(--cc-dark-green)' }}><Hash size={16}/> {title}</h2>
        <p className="text-sm" style={{ color: 'var(--cc-text-muted)' }}>Loading…</p>
      </div>
    );
  }

  const preview = buildPreview(cfg, cleanedPrefix || cfg.prefix, pad, seqInt, incMonth, incFy, incProject);
  const changed = cleanedPrefix !== cfg.prefix || pad !== cfg.pad || seqInt !== cfg.next_seq
    || incMonth !== cfg.include_month || incFy !== cfg.include_fy || incProject !== cfg.include_project;

  const Toggle = ({ label, checked, onChange, id }) => (
    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={`${tid}-${id}`} />
      {label}
    </label>
  );

  return (
    <div className="card p-6 mb-4" data-testid={`${tid}-card`}>
      <h2 className="font-head text-xl font-bold mb-1 flex items-center gap-2" style={{ color: 'var(--cc-dark-green)' }}><Hash size={16}/> {title}</h2>
      <p className="text-sm mb-3" style={{ color: 'var(--cc-text-muted)' }}>
        Control the prefix, serial padding and which parts (month, financial year, project name) appear in auto-generated numbers. The serial resets each financial year.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div className="rounded-md p-3" style={{ background: 'var(--cc-surface)' }}>
          <div className="uppercase tracking-wide" style={{ fontSize: '10px', color: 'var(--cc-text-muted)' }}>Currently next (FY {cfg.fy})</div>
          <div className="font-mono-data font-bold text-lg break-all" style={{ color: 'var(--cc-dark-green)' }} data-testid={`${tid}-current`}>{cfg.next_code}</div>
        </div>
        <div className="rounded-md p-3" style={{ background: 'var(--cc-surface)' }}>
          <div className="uppercase tracking-wide" style={{ fontSize: '10px', color: 'var(--cc-text-muted)' }}>After save, next will be</div>
          <div className="font-mono-data font-bold text-lg break-all" style={{ color: 'var(--cc-accent)' }} data-testid={`${tid}-preview`}>{preview}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="label text-xs">Prefix</label>
          <input type="text" className="input font-mono-data" value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder={kind === 'tax' ? 'CC-TAX' : 'CC-PIC'} data-testid={`${tid}-prefix`} />
        </div>
        <div>
          <label className="label text-xs">Padding (digits)</label>
          <input type="number" min={1} max={6} className="input font-mono-data" value={pad} onChange={(e) => setPad(parseInt(e.target.value, 10) || 1)} data-testid={`${tid}-pad`} />
        </div>
        <div>
          <label className="label text-xs">Next serial</label>
          <input type="number" min={1} className="input font-mono-data" value={nextSeq} onChange={(e) => setNextSeq(e.target.value)} data-testid={`${tid}-next-seq`} />
        </div>
      </div>

      <div className="flex flex-wrap gap-5 mt-3">
        <Toggle id="include-month" label={`Month (${cfg.month})`} checked={incMonth} onChange={setIncMonth} />
        <Toggle id="include-fy" label={`Financial year (${cfg.fy})`} checked={incFy} onChange={setIncFy} />
        <Toggle id="include-project" label="Project name" checked={incProject} onChange={setIncProject} />
      </div>

      <div className="flex justify-end mt-3">
        <button type="button" onClick={save} disabled={busy || !changed} className="btn btn-primary" data-testid={`${tid}-save`}>
          <Save size={14}/> {busy ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="text-[11px] mt-2 flex items-start gap-1" style={{ color: 'var(--cc-text-muted)' }}>
        <AlertTriangle size={11} className="mt-0.5"/>
        Existing invoice numbers are not changed. If the new starting number is already used by an invoice, the save will be refused.
      </div>

      {msg && (
        <div className="text-xs rounded-md p-2.5 mt-3 inline-flex items-center gap-1.5"
          style={msg.type === 'error' ? { background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5' } : { background: '#D1FAE5', color: '#065F46', border: '1px solid #34D399' }}
          data-testid={`${tid}-msg`}>
          {msg.type === 'success' && <CheckCircle2 size={12}/>}
          {msg.text}
        </div>
      )}
    </div>
  );
};

export default InvoiceSeriesCard;
